import type { useStytchB2BClient } from "@stytch/react/b2b";
import { productEventQueue } from "../util/productEventQueue";
import { activeTimeTracker } from "../hooks/useActiveTimeTracking";
import { requiredAuthMethods, signInRequirement, type SsoConnection } from "./authMethods";

// Provider-free project discovery and session exchange. The project switcher, the
// OAuth consent page and the access-recovery screen all go through this module, so
// the drain, quarantine and restore order exists in one place.

export type StytchClient = ReturnType<typeof useStytchB2BClient>;

/** The fields used here of Stytch's discovered organization (@stytch/core `DiscoveredOrganization`). */
export interface DiscoveredOrganization {
    organization: {
        organization_id: string;
        organization_name: string;
        organization_slug: string;
        allowed_auth_methods?: string[];
        sso_active_connections?: SsoConnection[];
        /** The connection single sign-on starts when the organization has several. */
        sso_default_connection_id?: string | null;
    };
    membership: {
        type: string;
    };
    /** False when this session's sign-in method is not one the organization accepts. */
    member_authenticated: boolean;
    primary_required?: { allowed_auth_methods?: string[] } | null;
}

/** The organization accepts only other sign-in methods, so the session did not enter it. */
export class StepUpRequired extends Error {
    constructor(readonly methods: string[]) {
        super(signInRequirement(methods));
        this.name = "StepUpRequired";
    }
}

/** Stytch answered the exchange without a session in the organization for a reason other than
 *  the sign-in method (a second factor). */
export class SwitchFailed extends Error {
    constructor() {
        super("Stytch answered the session exchange without a session in the organization.");
        this.name = "SwitchFailed";
    }
}

const MEMBERSHIP_TYPE_ACTIVE = "active_member";
const DEFAULT_SESSION_DURATION_MINUTES = 30 * 24 * 60; // 30 days
const ANALYTICS_DRAIN_TIMEOUT_MS = 1_500;

function getSessionDurationMinutes(expiresAt: string | undefined): number {
    if (!expiresAt) return DEFAULT_SESSION_DURATION_MINUTES;
    const remainingMs = new Date(expiresAt).getTime() - Date.now();
    const remainingMinutes = Math.ceil(remainingMs / (1000 * 60));
    // Clamp to reasonable range: at least 5 minutes, at most 30 days
    return Math.max(5, Math.min(remainingMinutes, DEFAULT_SESSION_DURATION_MINUTES));
}

/** Organizations the member can exchange their session into (active memberships only). */
export async function listActiveOrganizations(
    stytch: StytchClient,
): Promise<DiscoveredOrganization[]> {
    const response = await stytch.discovery.organizations.list();
    return response.discovered_organizations.filter(
        (org: DiscoveredOrganization) => org.membership.type === MEMBERSHIP_TYPE_ACTIVE,
    );
}

/**
 * Drains analytics for the outgoing organization, quarantines product-event delivery,
 * then exchanges the Stytch session. On failure the queue is restored to the outgoing
 * organization and the error is rethrown. The caller redirects only when this resolves,
 * which it does only when Stytch's answer has `member_authenticated === true`.
 *
 * An organization that needs another sign-in method throws `StepUpRequired` before any of
 * that, from discovery's answer: Stytch's SDK drops this session when an exchange answers
 * member_authenticated false, so the switch is not attempted when discovery already says so.
 * Discovery's answer is a snapshot (the organization's policy can change after the list
 * loaded, and it does not cover a second factor), so the exchange's own answer is checked
 * too: a refused exchange throws `StepUpRequired` or `SwitchFailed`, never resolves as a
 * switch into an organization the session did not enter.
 */
export async function exchangeSessionIntoOrganization(
    stytch: StytchClient,
    options: {
        organization: DiscoveredOrganization;
        currentOrganizationSlug: string | null | undefined;
        sessionExpiresAt: string | undefined;
    },
): Promise<void> {
    const { organization, currentOrganizationSlug, sessionExpiresAt } = options;
    const methods = requiredAuthMethods(organization);
    if (methods) throw new StepUpRequired(methods);
    try {
        if (currentOrganizationSlug) {
            activeTimeTracker.flush(false, true);
            await productEventQueue.drainOrganization(
                currentOrganizationSlug,
                true,
                ANALYTICS_DRAIN_TIMEOUT_MS,
            );
        }
        // Quarantine product-event delivery before Stytch changes the authenticated
        // organization. Leftover old-org rows remain in their org queue.
        productEventQueue.setActiveOrganization(null);
        const answer = await stytch.session.exchange({
            organization_id: organization.organization.organization_id,
            session_duration_minutes: getSessionDurationMinutes(sessionExpiresAt),
        });
        if (answer.member_authenticated !== true) {
            throw answer.mfa_required
                ? new SwitchFailed()
                : new StepUpRequired(requiredAuthMethods(answer) ?? []);
        }
    } catch (error) {
        productEventQueue.setActiveOrganization(currentOrganizationSlug ?? null);
        throw error;
    }
}
